// src/pages/outsourcing_management/requisitions/RequisitionWorkflowActions.tsx
//
// EE-207 #14, realigned in EE-253 - status-gated workflow action controls for
// the detail view: Submit, Reject (mandatory comment), Close, Cancel
// (mandatory reason code). Which buttons render is derived from
// requisitionWorkflow.getAllowedActions(status), which mirrors the backend's
// transition rules - never guessed per-component.
//
// Approve, Advance and Convert are deliberately absent - every step from
// approval onward happens on the linked Inquiry (the Inquiries panel below
// links straight to it). Approving is moving that Inquiry out of Draft, which
// syncs this requisition to Approved.
import { FunctionComponent, useContext, useState } from "react";
import { Button, CircularProgress, Stack, Tooltip } from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import BlockIcon from "@mui/icons-material/Block";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import SendIcon from "@mui/icons-material/Send";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { RequisitionStatusEnum, RequisitionType } from "types/RequisitionTypes";
import {
  useCancelRequisition,
  useCloseRequisition,
  useRequisitionOptions,
  useRejectRequisition,
  useSubmitRequisition,
} from "hooks/useRequisitions";
import {
  closeBlockedBecause,
  getActionButtonLabel,
  getAllowedActions,
} from "./requisitionWorkflow";
import RequisitionPromptDialog from "./RequisitionPromptDialog";

type PropsType = {
  requisition: RequisitionType;
  onChanged?: (requisition: RequisitionType) => void;
};

type OpenDialog = "reject" | "close" | "cancel" | null;

const RequisitionWorkflowActions: FunctionComponent<PropsType> = ({
  requisition,
  onChanged,
}) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const { data: options } = useRequisitionOptions(token);
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);

  const label = requisition.requisition_no ?? `#${requisition.id}`;

  const onMutationSuccess = (
    data: RequisitionType,
    message: string,
    type: "success" | "error" = "success"
  ) => {
    addToast({ type, message, keep: type === "error" });
    setOpenDialog(null);
    onChanged?.(data);
  };

  const onMutationError = (err: any, verb: string) => {
    addToast({
      type: "error",
      message: `Could not ${verb} requisition ${label}: ${err}`,
      keep: true,
    });
  };

  // submit commits the transition and then creates the Inquiry in a
  // try/except that logs the failure and returns anyway, so "submitted" and
  // "an inquiry exists" are two different facts. The response carries
  // `inquiries`, so say which one actually happened rather than promising the
  // inquiry unconditionally - without it there is nothing to approve, and the
  // requisition cannot move.
  const submitMutation = useSubmitRequisition(token, {
    onSuccess: (data) =>
      // EE-253 P2b: submit creates no Inquiry; the lines join
      // the buyer's Awaiting Inquiry pool.
      onMutationSuccess(
        data,
        `Requisition ${label} submitted - ${data.items.length} line(s) now in Awaiting Inquiry`
      ),
    onError: (err) => onMutationError(err, "submit"),
  });
  const rejectMutation = useRejectRequisition(token, {
    onSuccess: (data) =>
      onMutationSuccess(
        data,
        `Requisition ${label} sent back to Draft for edits`
      ),
    onError: (err) => onMutationError(err, "reject"),
  });
  const closeMutation = useCloseRequisition(token, {
    onSuccess: (data) => onMutationSuccess(data, `Requisition ${label} marked done`),
    onError: (err) => onMutationError(err, "close"),
  });
  const cancelMutation = useCancelRequisition(token, {
    onSuccess: (data) => onMutationSuccess(data, `Requisition ${label} cancelled`),
    onError: (err) => onMutationError(err, "cancel"),
  });

  const isBusy =
    submitMutation.isLoading ||
    rejectMutation.isLoading ||
    closeMutation.isLoading ||
    cancelMutation.isLoading;

  // EE-253 P2b: a line in an Inquiry past Draft is already
  // with a supplier, so the requisition can only be cancelled, not rejected.
  const allowedActions = getAllowedActions(
    requisition.status,
    requisition.inquiries,
    requisition
  ).filter((action) => action !== "reject" || requisition.can_reject !== false);
  const closeBlocked = closeBlockedBecause(
    requisition.status,
    requisition.inquiries,
    requisition
  );

  return (
    <>
      {/* Same vertical list of same-size buttons as the inquiry panel: which
          button sat beside which used to depend on how long its label
          happened to be. */}
      <Stack direction="column" spacing={1} sx={{ alignItems: "stretch" }}>
        {allowedActions.includes("submit") && (
          <Button
            fullWidth
            variant="contained"
            startIcon={submitMutation.isLoading ? <CircularProgress size={16} /> : <SendIcon />}
            disabled={isBusy}
            onClick={() => submitMutation.mutate(requisition.id)}
            sx={{
              background: "var(--mainYellow)",
              "&:hover": { color: "var(--mainYellow)", border: "1px solid var(--mainYellow)", background: "transparent" },
            }}
          >
            {getActionButtonLabel("submit")}
          </Button>
        )}
        {allowedActions.includes("reject") && (
          <Button
            fullWidth
            variant="outlined"
            color="warning"
            startIcon={<CancelOutlinedIcon />}
            disabled={isBusy}
            onClick={() => setOpenDialog("reject")}
          >
            {getActionButtonLabel("reject")}
          </Button>
        )}
        {(allowedActions.includes("close") || closeBlocked) && (
          <Tooltip title={closeBlocked ?? ""}>
            {/* A disabled button swallows its own hover events, so the
                tooltip needs a wrapper that is still listening. */}
            <span>
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<DoneAllIcon />}
                disabled={isBusy || !!closeBlocked}
                onClick={() => setOpenDialog("close")}
              >
                {getActionButtonLabel("close")}
              </Button>
            </span>
          </Tooltip>
        )}
        {allowedActions.includes("cancel") && (
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<BlockIcon />}
            disabled={isBusy}
            onClick={() => setOpenDialog("cancel")}
          >
            {getActionButtonLabel("cancel")}
          </Button>
        )}
      </Stack>

      <RequisitionPromptDialog
        open={openDialog === "reject"}
        title="Reject Requisition"
        description={`Send requisition ${label} back to Draft for edits. Its lines leave any Draft Inquiry; resubmitting puts them back in Awaiting Inquiry. A comment explaining why is required.`}
        confirmLabel="Reject"
        commentRequired
        isSubmitting={rejectMutation.isLoading}
        onClose={() => setOpenDialog(null)}
        onConfirm={(comment) => rejectMutation.mutate({ id: requisition.id, comment })}
      />

      <RequisitionPromptDialog
        open={openDialog === "close"}
        title="Mark Requisition Done"
        description={
          requisition.status === RequisitionStatusEnum.READY_TO_USE
            ? `Mark requisition ${label} done? The goods are registered and the request is complete. The linked inquiry is marked done with it.`
            : `Mark requisition ${label} done? Its inquiry has finished, so this request cannot go any further.`
        }
        confirmLabel="Mark Done"
        isSubmitting={closeMutation.isLoading}
        onClose={() => setOpenDialog(null)}
        onConfirm={(comment) =>
          closeMutation.mutate({ id: requisition.id, comment: comment || undefined })
        }
      />

      <RequisitionPromptDialog
        open={openDialog === "cancel"}
        title="Cancel Requisition"
        description="Cancelling closes this requisition. This cannot be undone."
        confirmLabel="Cancel Requisition"
        cancelLabel="Back"
        destructive
        reason={{
          options,
          label: "Cancellation Reason",
          missingText: "A cancellation reason is required",
        }}
        isSubmitting={cancelMutation.isLoading}
        onClose={() => setOpenDialog(null)}
        onConfirm={(comment, reasonCode) =>
          cancelMutation.mutate({
            id: requisition.id,
            reasonCode: reasonCode!,
            comment: comment || undefined,
          })
        }
      />
    </>
  );
};

export default RequisitionWorkflowActions;
